import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Grid,
    IconButton,
    Input,
    Option,
    Select,
    Sheet,
    Stack,
    Typography,
} from '@mui/joy';
import {
    ArrowBack,
    DeviceHub,
    Info,
    KeyboardArrowLeft,
    KeyboardArrowRight,
    Person,
    Refresh,
    VideoLibrary,
    Warning,
} from '@mui/icons-material';
import { listFootageShares } from '../services/observabilityAdminApi';
import FootageSharesTable, { FOOTAGE_SHARE_STATUSES } from '../components/FootageSharesTable';

const PAGE_SIZES = [25, 50, 100];
const EMPTY_FILTERS = { status: '', hub_id: '', client_id: '' };

/** All footage shares across hubs, newest first, filtered by status, hub and client. */
const FootageSharesPage = () => {
    const { session } = useAuth();
    const navigate = useNavigate();

    // Draft filters are what the inputs show; applied filters drive the query.
    const [draft, setDraft] = useState(EMPTY_FILTERS);
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [limit, setLimit] = useState(50);
    const [offset, setOffset] = useState(0);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [forbidden, setForbidden] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);

    const load = useCallback(async () => {
        if (!session?.accessToken) return;
        try {
            setLoading(true);
            setError(null);
            setForbidden(false);
            const { ok, status, body } = await listFootageShares({ ...filters, limit, offset }, session);
            if (status === 403) {
                setForbidden(true);
                setItems([]);
                return;
            }
            if (!ok) {
                setError(body?.error || `Failed to load footage shares (${status}).`);
                return;
            }
            setItems(body?.items || []);
        } catch (e) {
            setError('Unable to reach the admin API.');
        } finally {
            setLoading(false);
        }
    }, [session, filters, limit, offset]);

    useEffect(() => {
        load();
    }, [load, reloadKey]);

    const applyFilters = () => {
        setOffset(0);
        setFilters(draft);
    };

    const clearFilters = () => {
        setDraft(EMPTY_FILTERS);
        setOffset(0);
        setFilters(EMPTY_FILTERS);
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') applyFilters();
    };

    // The API has no total count: there may be a next page when this one is full.
    const hasNext = items.length >= limit;
    const rangeStart = items.length === 0 ? 0 : offset + 1;
    const rangeEnd = offset + items.length;

    return (
        <Sheet sx={{ p: 4, maxWidth: '1400px', mx: 'auto' }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                    <IconButton variant="outlined" onClick={() => navigate('/admin')} sx={{ borderRadius: '50%' }}>
                        <ArrowBack />
                    </IconButton>
                    <VideoLibrary sx={{ fontSize: 32, color: 'primary.500' }} />
                    <Box>
                        <Typography level="h2" sx={{ mb: 0.5 }}>
                            Footage Shares
                        </Typography>
                        <Typography level="body-lg" textColor="neutral.600">
                            Owner-consented uploads of hub recordings to the verification tool
                        </Typography>
                    </Box>
                </Stack>
            </Box>

            {/* Filters */}
            <Card variant="outlined" sx={{ mb: 4 }}>
                <CardContent>
                    <Grid container spacing={2} alignItems="end">
                        <Grid xs={12} sm={6} md={3}>
                            <Typography level="body-sm" sx={{ mb: 1 }}>Status</Typography>
                            <Select
                                value={draft.status}
                                onChange={(_, value) => setDraft({ ...draft, status: value ?? '' })}
                                disabled={loading}
                            >
                                <Option value="">All statuses</Option>
                                {FOOTAGE_SHARE_STATUSES.map((status) => (
                                    <Option key={status} value={status}>{status}</Option>
                                ))}
                            </Select>
                        </Grid>
                        <Grid xs={12} sm={6} md={3}>
                            <Typography level="body-sm" sx={{ mb: 1 }}>Hub ID</Typography>
                            <Input
                                placeholder="Any hub"
                                type="number"
                                value={draft.hub_id}
                                onChange={(e) => setDraft({ ...draft, hub_id: e.target.value })}
                                onKeyPress={handleKeyPress}
                                startDecorator={<DeviceHub />}
                                disabled={loading}
                            />
                        </Grid>
                        <Grid xs={12} sm={6} md={3}>
                            <Typography level="body-sm" sx={{ mb: 1 }}>Client ID</Typography>
                            <Input
                                placeholder="Any client"
                                type="number"
                                value={draft.client_id}
                                onChange={(e) => setDraft({ ...draft, client_id: e.target.value })}
                                onKeyPress={handleKeyPress}
                                startDecorator={<Person />}
                                disabled={loading}
                            />
                        </Grid>
                        <Grid xs={12} sm={6} md={3}>
                            <Stack direction="row" spacing={1}>
                                <Button onClick={applyFilters} variant="solid" loading={loading} sx={{ flex: 1 }}>
                                    Apply
                                </Button>
                                <Button onClick={clearFilters} variant="outlined" color="neutral" disabled={loading}>
                                    Clear
                                </Button>
                                <IconButton variant="outlined" onClick={() => setReloadKey((k) => k + 1)} disabled={loading}>
                                    <Refresh />
                                </IconButton>
                            </Stack>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {forbidden ? (
                <Alert color="warning" variant="soft" startDecorator={<Warning />}>
                    <Box>
                        <Typography level="title-sm">Developer access required</Typography>
                        <Typography level="body-sm" textColor="neutral.600">
                            You need Developer Access (read or write) to view footage shares.
                            Ask an administrator to grant the “Developer Access” permission.
                        </Typography>
                    </Box>
                </Alert>
            ) : error ? (
                <Alert color="danger" variant="soft">{error}</Alert>
            ) : loading && items.length === 0 ? (
                <Card variant="soft" sx={{ p: 3, textAlign: 'center' }}>
                    <CircularProgress size="sm" />
                </Card>
            ) : items.length === 0 ? (
                <Card variant="soft" sx={{ p: 4, textAlign: 'center' }}>
                    <Info sx={{ fontSize: 48, color: 'neutral.400', mb: 2 }} />
                    <Typography level="h4" sx={{ mb: 1 }}>No footage shares</Typography>
                    <Typography level="body-sm" textColor="neutral.500">
                        {offset > 0 ? 'No more shares on this page' : 'Nothing matches the current filters'}
                    </Typography>
                </Card>
            ) : (
                <Box>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                        <Typography level="h4">Shares</Typography>
                        <Chip color="primary" variant="soft">
                            {rangeStart}–{rangeEnd}{hasNext ? '+' : ''}
                        </Chip>
                    </Stack>
                    <FootageSharesTable items={items} />
                </Box>
            )}

            {/* Paging: limit/offset; shown whenever a page may exist on either side */}
            {!forbidden && !error && (offset > 0 || hasNext) && (
                <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1} sx={{ mt: 2 }}>
                    <Typography level="body-sm" textColor="neutral.500">Per page</Typography>
                    <Select
                        size="sm"
                        value={limit}
                        onChange={(_, value) => { setOffset(0); setLimit(value ?? 50); }}
                        disabled={loading}
                    >
                        {PAGE_SIZES.map((size) => (
                            <Option key={size} value={size}>{size}</Option>
                        ))}
                    </Select>
                    <IconButton
                        size="sm"
                        variant="plain"
                        disabled={loading || offset === 0}
                        onClick={() => setOffset(Math.max(0, offset - limit))}
                        aria-label="Previous page"
                    >
                        <KeyboardArrowLeft />
                    </IconButton>
                    <IconButton
                        size="sm"
                        variant="plain"
                        disabled={loading || !hasNext}
                        onClick={() => setOffset(offset + limit)}
                        aria-label="Next page"
                    >
                        <KeyboardArrowRight />
                    </IconButton>
                </Stack>
            )}
        </Sheet>
    );
};

export default FootageSharesPage;
